<template>
  <v-container fluid grid-list-lg>
    <AccountTerminalDebug :terminal="selectedTerminal"></AccountTerminalDebug>

    <div class="d-inline-flex" v-if="$route.name === 'ChargeACH' && terminalsACHCanCharge.length === 1">
      <span><h1 class="title font-weight-black mr-2"><span class="grey--text">ACH</span> Payment:</h1></span>
      <span class="subheading grey--text">{{ selectedTerminal.name }}</span>
    </div>

    <div class="d-inline-flex" v-if="$route.name === 'ChargeCard' && terminalsCardCanCharge.length === 1">
      <span><h1 class="title font-weight-black mr-2"><span class="grey--text">Credit</span> Payment:</h1></span>
      <span class="subheading grey--text">{{ selectedTerminal.name }}</span>
    </div>

    <TerminalSelect ref="terminal" v-model="selectedTerminal" v-bind:processorId="installmentProcessorFilter"></TerminalSelect>

    <v-layout row wrap ma-0 mb-2 pa-0 v-if="(this.$route.query.hasOwnProperty('r') || this.$route.query.hasOwnProperty('i')) && (this.$route.name === 'ChargeACH' && terminalsACHCanCharge.length > 1 || this.$route.name === 'ChargeCard' && terminalsCardCanCharge.length > 1)">
      <v-flex xs6 pt-0>
        <v-layout>
          <template v-if="this.$route.query.hasOwnProperty('r')">
            <h2><span class="grey--text">{{ $route.name === 'ChargeACH' ? 'ACH' : 'Card' }}</span> Recurring</h2>
            <Lightbulb medium>
              <template v-slot:message>
                <span>This feature is for fixed amount transactions that are charged on a weekly or monthly ongoing basis.</span>
              </template>
            </Lightbulb>
          </template>
          <template v-if="this.$route.query.hasOwnProperty('i')">
            <h2><span class="grey--text">{{ $route.name === 'ChargeACH' ? 'ACH' : 'Card' }}</span> Installment</h2>
            <Lightbulb medium>
              <template v-slot:message>
                <span>This feature is for fixed amount transactions that are charged weekly or monthly for a set amount OWED and a set time frame.</span>
              </template>
            </Lightbulb>
          </template>
        </v-layout>
      </v-flex>

      <v-flex xs6>
        <v-layout justify-end align-end>
          <span class="red--text caption">* <span class="font-weight-bold">Red</span> Fields Required for Processing</span>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-form ref="form1" @submit.prevent>
      <v-layout row wrap v-if="!this.$route.query.hasOwnProperty('r') && !this.$route.query.hasOwnProperty('i')">
        <v-flex xs6 sm2>
          <AmountThreshold
            v-model="fields.amount"
            :disabled="waivePrevious"
            :required="!waivePrevious"
            :error-messages="getRequiredField('amount')"
          ></AmountThreshold>
          <v-layout row class="ma-0 mt-1" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !fields.waive_fee">
            <v-flex grow class="pa-1 pl-2 caption primary white--text">
              ${{ priceAdjustmentFee.cash_discount }}
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 pt-2 caption primary text-truncate white--text">
              Price Adj Fee
            </v-flex>
          </v-layout>
          <v-layout row class="ma-0 mt-1" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !fields.waive_fee">
            <v-flex grow class="pa-1 pl-2 caption primary white--text">
              ${{ priceAdjustmentFee.total_amount_with_cash_discount }}
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 pt-2 caption primary text-truncate white--text">
              Total Amount
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs6 sm2 v-if="fields.close_date">
          <v-menu
            v-model="closeDateModal"
            ref="closeDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.close_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Post Date"
              slot="activator"
              v-model="formattedCloseDate"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.close_date"
              :min="minimumCloseDate"
              @input="$refs.closeDateModal.save(fields.close_date)"
            ></v-date-picker>
          </v-menu>
        </v-flex>

        <v-flex xs12 sm10 shrink>
          <v-layout row wrap align-center fill-height :justify-end="$vuetify.breakpoint.smAndUp" :justify-center="$vuetify.breakpoint.xsOnly">
            <v-flex shrink pr-0>
              <v-btn color="primary" :to="{query: Object.assign({}, this.$route.query, {r:1})}" v-if="hasPermission('has_recurring_options')" id="recurring-button">
                Recurring Payment
                <v-divider vertical class="ml-3 mr-2" v-if="hasPermission('has_inline_help')"></v-divider>
                <Lightbulb color="primary--text">
                  <template v-slot:message>
                    <span>This feature is for fixed amount transactions that are charged on a weekly or monthly ongoing basis.</span>
                  </template>
                </Lightbulb>
              </v-btn>
            </v-flex>
            <v-flex shrink pr-0>
              <v-btn color="primary" :to="{query: Object.assign({}, this.$route.query, {i:1})}" v-if="hasPermission('has_installment_options') && !($route.name === 'ChargeACH' && hasPermission('has_ach_schedules'))">
                Installment Payment
                <v-divider vertical class="ml-3 mr-2" v-if="hasPermission('has_inline_help')"></v-divider>
                <Lightbulb color="primary--text">
                  <template v-slot:message>
                    <span>This feature is for fixed amount transactions that are charged weekly or monthly for a set amount OWED and a set time frame.</span>
                  </template>
                </Lightbulb>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="this.$route.query.hasOwnProperty('r')" align-start>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">1</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Amount</span>
            </v-flex>
          </v-layout>

          <AmountThreshold
            v-model="fields.amount"
            :disabled="waivePrevious || recurringLocked"
            :required="!waivePrevious"
            :error-messages="getRequiredField('amount')"
          ></AmountThreshold>

          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=true" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 pt-2 pl-2 caption white primary--text text-truncate">
              Show Price Adj Fee / Total Price
            </v-flex>
            <v-flex shrink class="pa-0 white">
              <v-divider vertical class="primary"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption white primary--text">
              &or;
            </v-flex>
          </v-layout>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=false" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 flex-column pl-2 caption primary white--text">
              <div class="d-block">
                <p class="mb-0">
                  Price Adjustment: {{ getTotalWithCashDiscount(fields.amount).priceAdjustment}}
                </p>
                <v-divider class="my-1" />
                <p class="mb-0">
                  Total Price: {{ getTotalWithCashDiscount(fields.amount).total }}
                </p>
              </div>
            </v-flex>
            <v-flex shrink class="pa-0 primary">
              <v-divider vertical class="white"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption primary white--text">
              &and;
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">2</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Frequency</span>
            </v-flex>
          </v-layout>
          <v-select
            label="Frequency"
            v-model="fields.recurring"
            :items="recurringTypes"
            :rules="recurringFrequencyRules"
            append-icon="fa-refresh"
            box
            hide-details
            :disabled="waivePrevious || recurringLocked"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">3</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Start Date</span>
            </v-flex>
          </v-layout>
          <v-text-field
            v-if="waivePrevious || recurringLocked"
            label="Start Date"
            v-model="formattedRecurringStartDate"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-menu
            v-else
            v-model="startDateModal"
            ref="startDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_start_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Start Date"
              slot="activator"
              v-model="formattedRecurringStartDate"
              :rules="recurringStartRules"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_start_date"
              :min="minimumRecurringStartDate"
              @input="$refs.startDateModal.save(fields.recurring_start_date); handleChange('start_date');"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">4</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">End Date</span>
            </v-flex>
          </v-layout>
          <v-text-field
            v-if="waivePrevious || fields.recurring_ongoing || recurringLocked"
            label="End Date"
            v-model="formattedRecurringEndDate"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-menu
            v-else
            v-model="endDateModal"
            ref="endDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_end_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="End Date"
              slot="activator"
              v-model="formattedRecurringEndDate"
              :rules="recurringEndRules"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_end_date"
              :allowed-dates="recurringAllowedEndDates"
              :min="minimumRecurringEndDate"
              @input="$refs.endDateModal.save(fields.recurring_end_date); handleChange('end_date');"
            ></v-date-picker>
          </v-menu>
          <v-checkbox
            label="Ongoing"
            v-model="fields.recurring_ongoing"
            color="primary"
            box
            hide-details
            class="ma-0"
            :disabled="waivePrevious || recurringLocked"
          ></v-checkbox>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">5</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text"># of Payments</span>
            </v-flex>
          </v-layout>
          <v-text-field
            label="# of Payments"
            type="number"
            v-model="fields.recurring_payments"
            :disabled="!fields.recurring_start_date || waivePrevious || fields.recurring_ongoing || recurringLocked"
            append-icon="fa-hashtag"
            @input="setEndDate"
            box
            hide-details
            :readonly="fields.recurring_ongoing"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 md2 pt-5>
          <v-btn color="primary" @click="openConfirmPaymentPlanModal" v-if="!recurringLocked">Confirm</v-btn>
          <v-btn color="error" @click="confirmPaymentPlanModal=true" v-if="recurringLocked">Edit</v-btn>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="this.$route.query.hasOwnProperty('i')" align-start>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">1</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Amount</span>
            </v-flex>
          </v-layout>
          <AmountThreshold
            v-model="fields.recurring_total_amount"
            :disabled="waivePrevious || recurringLocked"
            :required="!waivePrevious"
            :error-messages="getRequiredField('recurring_total_amount')"
          ></AmountThreshold>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">2</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Downpayment</span>
            </v-flex>
          </v-layout>
          <v-currency-field
            label="Downpayment"
            type="tel"
            v-model.lazy="fields.amount"
            v-bind="currencyFormat"
            append-icon="fa-dollar"
            :disabled="waivePrevious || recurringLocked"
            :required="!waivePrevious"
            :rules="downpaymentRules"
            maxlength="9"
            box
            hide-details
          ></v-currency-field>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=true" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 pt-2 pl-2 caption white primary--text text-truncate">
              Show Price Adj Fee / Total Price
            </v-flex>
            <v-flex shrink class="pa-0 white">
              <v-divider vertical class="primary"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption white primary--text">
              &or;
            </v-flex>
          </v-layout>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=false" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 flex-column pl-2 caption primary white--text">
              <div class="d-block">
                <p class="mb-0">
                  Price Adjustment: {{ getTotalWithCashDiscount(fields.amount).priceAdjustment }}
                </p>
                <v-divider class="my-1" />
                <p class="mb-0">
                  Total Price: {{ getTotalWithCashDiscount(fields.amount).total }}
                </p>
              </div>
            </v-flex>
            <v-flex shrink class="pa-0 primary">
              <v-divider vertical class="white"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption primary white--text">
              &and;
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 mt-2 mb-2>
              <span class="font-weight-bold secondary--text">Remaining Balance</span>
            </v-flex>
          </v-layout>
          <v-text-field
            label="Remaining Balance"
            type="tel"
            v-model="recurringRemainingBalance"
            :disabled="waivePrevious || recurringLocked"
            append-icon="fa-dollar"
            @input="handleChange('balance')"
            maxlength="9"
            box
            hide-details
            readonly
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">3</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Frequency</span>
            </v-flex>
          </v-layout>
          <v-select
            label="Frequency"
            v-model="fields.recurring"
            :disabled="waivePrevious || recurringLocked"
            :items="recurringTypes"
            :rules="recurringFrequencyRules"
            append-icon="fa-refresh"
            @input="handleChange('frequency')"
            box
            hide-details
          ></v-select>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">4</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text"># of Payments</span>
            </v-flex>
          </v-layout>
          <v-text-field
            label="# of Payments"
            type="number"
            v-model="fields.recurring_payments"
            :disabled="!fields.recurring_start_date || waivePrevious || fields.recurring_ongoing || recurringLocked"
            append-icon="fa-hashtag"
            @input="handleChange('payments')"
            box
            hide-details
            :readonly="fields.recurring_ongoing"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 md2 pt-5 v-if="$route.query.hasOwnProperty('i') && $route.query.i === 2">
          <v-btn @click="recurringParentModal=true">Previous</v-btn>
        </v-flex>
        <v-flex md2 class="hidden-sm-and-down" v-else>
        </v-flex>
        <v-flex md2 class="hidden-sm-and-down">
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">5</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Installments</span>
            </v-flex>
          </v-layout>
          <v-currency-field
            label="Installments"
            type="tel"
            v-bind="currencyFormat"
            v-model="fields.recurring_amount"
            :disabled="waivePrevious || recurringLocked"
            :rules="recurringAmountRules"
            append-icon="fa-dollar"
            @input="handleChange('amount')"
            box
            hide-details
          ></v-currency-field>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=true" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 pt-2 pl-2 caption white primary--text text-truncate">
              Show Price Adj Fee / Total Price
            </v-flex>
            <v-flex shrink class="pa-0 white">
              <v-divider vertical class="primary"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption white primary--text">
              &or;
            </v-flex>
          </v-layout>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=false" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 flex-column pl-2 caption primary white--text">
              <div class="d-block">
                <p class="mb-0">
                  Price Adjustment: {{getTotalWithCashDiscount(fields.recurring_amount).priceAdjustment }}
                </p>
                <v-divider class="my-1" />
                <p class="mb-0">
                  Total Price: {{ getTotalWithCashDiscount(fields.recurring_amount).total }}
                </p>
              </div>
            </v-flex>
            <v-flex shrink class="pa-0 primary">
              <v-divider vertical class="white"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption primary white--text">
              &and;
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 shrink>
              <v-chip disabled class="pa-0 ma-0 font-weight-bold primary white--text">6</v-chip>
            </v-flex>
            <v-flex>
              <span class="font-weight-bold primary--text">Start Date</span>
            </v-flex>
          </v-layout>
          <v-text-field
            v-if="waivePrevious || recurringLocked"
            label="Start Date"
            v-model="formattedRecurringStartDate"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-menu
            v-else
            v-model="startDateModal"
            ref="startDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_start_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Start Date"
              slot="activator"
              v-model="formattedRecurringStartDate"
              :rules="recurringStartRules"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_start_date"
              :min="minimumRecurringStartDate"
              @input="$refs.startDateModal.save(fields.recurring_start_date); handleChange('start_date')"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs sm4 md2>
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 mt-2 mb-2>
              <span class="font-weight-bold secondary--text">End Date</span>
            </v-flex>
          </v-layout>
          <v-text-field
            label="End Date"
            v-model="formattedRecurringEndDate"
            append-icon="fa-calendar"
            :disabled="waivePrevious || recurringLocked"
            box
            hide-details
            readonly
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 md2 v-if="fields.recurring_last_amount">
          <v-layout row align-center fill-height>
            <v-flex pa-0 pl-2 mt-2 mb-2>
              <span class="font-weight-bold secondary--text">Last Payment</span>
            </v-flex>
          </v-layout>
          <v-currency-field
            label="Last Payment"
            type="tel"
            v-model.lazy="fields.recurring_last_amount"
            v-bind="currencyFormat"
            :disabled="waivePrevious || recurringLocked"
            append-icon="fa-dollar"
            box
            hide-details
            readonly
          ></v-currency-field>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=true" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 pt-2 pl-2 caption white primary--text text-truncate">
              Show Price Adj Fee / Total Price
            </v-flex>
            <v-flex shrink class="pa-0 white">
              <v-divider vertical class="primary"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption white primary--text">
              &or;
            </v-flex>
          </v-layout>
          <v-layout row class="ma-0 mt-1" style="border: 1px solid #44a9e2;cursor: pointer;" @click="showPriceAdjustmentFee=false" v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && showPriceAdjustmentFee && !fields.waive_fee">
            <v-flex class="pa-1 flex-column pl-2 caption primary white--text">
              <div class="d-block">
                <p class="mb-0">
                  Price Adjustment: {{ getTotalWithCashDiscount(fields.recurring_last_amount).priceAdjustment }}
                </p>
                <v-divider class="my-1" />
                <p class="mb-0">
                  Total Price: {{ getTotalWithCashDiscount(fields.recurring_last_amount).total }}
                </p>
              </div>
            </v-flex>
            <v-flex shrink class="pa-0 primary">
              <v-divider vertical class="white"></v-divider>
            </v-flex>
            <v-flex shrink class="pa-1 pl-2 pr-2 caption primary white--text">
              &and;
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 sm4 md2 pt-5>
          <v-btn color="primary" @click="openConfirmPaymentPlanModal" v-if="!recurringLocked">Confirm</v-btn>
          <v-btn color="error" @click="confirmPaymentPlanModal=true" v-if="recurringLocked">Edit</v-btn>
        </v-flex>
      </v-layout>
    </v-form>

    <v-layout row wrap justify-end pr-2>
        <v-flex shrink v-if="!this.$route.query.hasOwnProperty('i') && !this.$route.query.hasOwnProperty('r')">
          <span class="red--text caption">* <span class="font-weight-bold">Red</span> Fields Required for Processing</span>
        </v-flex>
    </v-layout>

    <v-divider class="primary mt-2 mb-2"></v-divider>

    <v-form ref="form2" @submit.prevent>
      <v-layout row wrap v-if="$route.name === 'ChargeCard'">
        <template v-if="$route.query.hasOwnProperty('transaction') || $route.query.hasOwnProperty('rerun')">
          <v-flex xs6 sm4>
            <v-checkbox
              label="New Card"
              v-model="newPaymentInfo"
              color="primary"
              box
              hide-details
              class="ma-0"
              :disabled="!recurringLocked"
              @click.native.stop="clearToken"
            ></v-checkbox>
          </v-flex>
        </template>
      </v-layout>

      <v-layout row wrap v-if="$route.name === 'ChargeCard'">
        <v-flex xs12 sm4>
          <v-text-field
            v-if="parentId"
            label="Card Holder Name"
            v-model="parentDetails.billing_name"
            append-icon="fa-user"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Card Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            :disabled="!recurringLocked"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_name')"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            v-if="parentId"
            label="Card Number"
            v-model="parentCardNumber"
            :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
            box
            hide-details
            disabled
          >
            <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
          </v-text-field>
          <v-text-field
            v-else
            label="Card Number"
            v-model="fields.card_number"
            :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
            :disabled="!recurringLocked"
            :mask="cardMask"
            :rules="cardRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_number')"
          >
            <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            v-if="parentId"
            label="Expiration"
            v-model="parentExpiration"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Expiration"
            type="tel"
            v-model="fields.card_expiration"
            append-icon="fa-calendar"
            mask="## / ##"
            :disabled="!recurringLocked"
            :rules="expirationRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_expiration')"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            v-if="parentId"
            label="CVV"
            value="***"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="CVV"
            type="test"
            v-model="fields.card_cvv"
            :disabled="!recurringLocked"
            :rules="cvvRules"
            :mask="cvvMask"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_cvv')"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="$route.name === 'ChargeACH'">
        <template v-if="$route.query.hasOwnProperty('transaction') || $route.query.hasOwnProperty('rerun')">
          <v-flex xs12>
            <v-checkbox
              label="New ACH Info"
              v-model="newPaymentInfo"
              color="primary"
              box
              hide-details
              class="ma-0"
              :disabled="!recurringLocked"
              @click.native.stop="clearToken"
            ></v-checkbox>
          </v-flex>
        </template>

        <v-flex xs6 sm4>
          <v-text-field
            v-if="parentId"
            label="Account Holder Name"
            v-model="parentDetails.billing_name"
            append-icon="fa-user"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Account Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :disabled="!recurringLocked"
            :rules="nameRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_name')"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            v-if="parentId"
            label="Routing Number"
            v-model="parentDetails.ach_routing_number"
            append-icon="fa-exchange"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Routing Number"
            v-model="fields.ach_routing_number"
            append-icon="fa-exchange"
            :disabled="!recurringLocked"
            :rules="routingRules"
            mask="#########"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_routing_number')"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            v-if="parentId"
            label="Account Number"
            v-model="parentAccountNumber"
            append-icon="fa-institution"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Account Number"
            v-model="fields.ach_account_number"
            append-icon="fa-institution"
            :disabled="!recurringLocked"
            :rules="accountRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_account_number')"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4>
          <v-select
            v-if="parentId"
            label="Type"
            v-model="parentDetails.ach_account_type"
            :items="achTypes"
            box
            hide-details
            disabled
          ></v-select>
          <v-select
            v-else
            label="Type"
            v-model="fields.ach_type"
            :disabled="!recurringLocked"
            :items="achTypes"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_type')"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm4 pb-0 v-if="!parentId" offset-sm4>
          <v-text-field
            label="Re-enter Account Number"
            v-model="fields.ach_account_number_verify"
            append-icon="fa-institution"
            :disabled="!recurringLocked"
            :rules="accountVerifyRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_account_number_verify')"
            onpaste="return false;"
            ondrop="return false;"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs12 sm4>
          <v-text-field
            label="Address"
            v-model="fields.billing_address"
            :disabled="waivePrevious || !recurringLocked"
            append-icon="fa-home"
            box
            hide-details
            required
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="City"
            v-model="fields.billing_city"
            :disabled="waivePrevious || !recurringLocked"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <StateField
            v-model="fields.billing_state"
            :disabled="waivePrevious || !recurringLocked"
            :textfield="fields.billing_country !== 'USA'"
          ></StateField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Postal Code"
            v-model="fields.billing_postalcode"
            :disabled="waivePrevious || !recurringLocked"
            :rules="$route.name === 'ChargeCard' ? postalcodeRules : []"
            box
            hide-details
            :error-messages="$route.name === 'ChargeCard' ? getRequiredField('billing_postalcode') : ''"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Phone"
            v-model="fields.billing_phone"
            :disabled="waivePrevious || !recurringLocked"
            mask="(###) ###-####"
            append-icon="fa-phone"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Email"
            v-model="fields.billing_email"
            :disabled="waivePrevious || !recurringLocked"
            append-icon="fa-at"
            :rules="emailRules"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <CountryField
            v-model="fields.billing_country"
            :disabled="waivePrevious || !recurringLocked || $route.name === 'ChargeACH'"
            icon="fa-globe"
          ></CountryField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Customer PO"
            v-model="fields.customer_po"
            :disabled="waivePrevious || !recurringLocked"
            append-icon="fa-file-text-o"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Used to relate transactions to your management software.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 1"
            v-model="fields.custom_field_1"
            :disabled="!recurringLocked"
            maxlength="30"
            append-icon="fa-edit"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 2"
            v-model="fields.custom_field_2"
            :disabled="!recurringLocked"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 3"
            v-model="fields.custom_field_3"
            :disabled="!recurringLocked"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs12>
          <v-textarea
            label="Notes (Internal Use Only will not print on customer receipt)"
            v-model="fields.notes"
            :disabled="!recurringLocked"
            box
            hide-details
            height
            rows="1"
          ></v-textarea>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap align-center fill-height>
        <template v-if="$route.name === 'ChargeCard'">
          <v-flex xs12 sm4>
          </v-flex>

          <template v-if="waiveHealthCard || waiveLevel">
            <v-flex xs12 sm4 class="dark warning" pa-4>
              <v-layout row wrap justify-center>
                {{ waiveMessage }}
              </v-layout>
            </v-flex>
          </template>

          <template v-if="waivePrevious">
            <v-flex xs12 :sm8="!waiveHealthCard" :sm4="waiveHealthCard">
              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="validateBeforeProcessing" :disabled="isProcessing || !recurringLocked">process</v-btn>
              </v-layout>
            </v-flex>
          </template>

          <template v-else>
            <v-flex xs12 :sm8="!waiveHealthCard && !waiveLevel" :sm4="waiveHealthCard || waiveLevel">
              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="validateBeforeProcessing" :disabled="isProcessing || !recurringLocked">process</v-btn>
                <v-btn @click="reset" v-if="!parentId">clear</v-btn>
              </v-layout>
            </v-flex>
          </template>
        </template>

        <template v-if="$route.name === 'ChargeACH'">
          <v-flex xs12 sm4>
          </v-flex>
          <v-flex xs12 sm4>
            <v-layout row wrap justify-end v-if="accountSelected.permissions !== null && accountSelected.permissions.hasOwnProperty('can_use_nacha_authorization') && accountSelected.permissions.can_use_nacha_authorization">
              <v-select
                label="ACH Authorization"
                v-model="fields.ach_authorization"
                :rules="achAuthRules"
                :items="achAuthOptions"
                box
                hide-details
                required
                :error-messages="getRequiredField('ach_authorization')"
              >
                <div slot="append-outer">
                  <Lightbulb medium>
                    <template v-slot:message>
                      <span>
                        For Authorization on File and Print and Sign now Option Transaction will process instantly.
                        <br />
                        For Email Authorization transaction will only process when customer receives email and clicks Authorize.
                      </span>
                    </template>
                  </Lightbulb>
                </div>
              </v-select>
            </v-layout>
          </v-flex>
          <v-flex xs12 sm4>
            <v-layout row wrap justify-end>
              <v-btn color="primary" @click="validateBeforeProcessing" :disabled="isProcessing || !recurringLocked">process</v-btn>
              <v-btn @click="reset" v-if="!parentId">clear</v-btn>
            </v-layout>
          </v-flex>
        </template>
      </v-layout>
    </v-form>

    <v-expansion-panel expand v-model="panel" v-if="debug">
      <v-expansion-panel-content>
        <div slot="header">Responses</div>
        <v-layout column ma-2 v-for="(response, key) in responses" :key="response.time">
          <v-flex wrap>
            <v-toolbar dark dense color="pink">
              <v-toolbar-title>{{ response.time | localeDate }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon>
                <v-icon>fa-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card dark class="pr-2 pb-2 pl-2">
              <v-card-text class="ma-0 pa-0">
                <v-tabs dark>
                  <v-tab :href="`#tab-${key}-response`">Response</v-tab>
                  <v-tab :href="`#tab-${key}-request`">Request</v-tab>
                  <v-tab :href="`#tab-${key}-error`">Error</v-tab>
                  <v-tab :href="`#tab-${key}-rawresponse`">Raw Response</v-tab>
                  <v-tab :href="`#tab-${key}-rawrequest`">Raw Request</v-tab>
                  <v-tabs-items style="max-height: 400px; overflow: auto;">
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-response`" :key="`tab-${key}-response`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.response | pretty }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-request`" :key="`tab-${key}-request`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.request | pretty }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-error`" :key="`tab-${key}-error`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.error | pretty }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-rawresponse`" :key="`tab-${key}-rawresponse`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.raw_response }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-rawrequest`" :key="`tab-${key}-rawrequest`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.raw_request }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                  </v-tabs-items>
                </v-tabs>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-dialog v-model="waiveHSAConfirmationModal" maxWidth="500px" persistent @keydown.esc.prevent="closeHSAConfirmationModal" @input="v => v || closeHSAConfirmationModal">
      <v-toolbar dense>
        <v-toolbar-title><v-icon>fa-exclamation-triangle</v-icon> Insurance Adjustment Fee</v-toolbar-title>
      </v-toolbar>

      <v-card>
        <v-card-text fill-height>
          <v-layout row wrap align-center>
            <div>
              <span class="font-weight-bold">Attention:</span>
              <br />
              This feature is for processing insurance payments and FSA or HSA cards with <span class="font-weight-black d-inline">fixed amounts</span>.
            </div>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeHSAConfirmationModal">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="waiveConfirmationModal" maxWidth="500px" persistent>
      <v-toolbar dense>
        <v-toolbar-title><v-icon>fa-exclamation-triangle</v-icon> Price Adjustment Fee</v-toolbar-title>
      </v-toolbar>

      <v-card>
        <v-form ref="formPassword" lazy-validation @submit.prevent @keyup.native.enter="checkWaivePassword">
          <v-card-text fill-height>
            <v-layout row wrap align-center>
              <template v-if="waiveLevel === 3">
                <div class="pa-2">
                  <div class="font-weight-bold pb-2">Attention:</div>

                  Please be advised that you are about to open the payment page where the price adjustment is waived for your debit card customers. By continuing, the amount processed with this transaction will <span class="font-weight-black d-inline">incur a {{ waivedDebitFeeRate }}% fee</span>. This will be billed to you, the merchant, at the end of each month.
                </div>
              </template>
              <template v-else>
                <div class="pa-2">
                  <div class="font-weight-bold pb-2">Attention:</div>

                  Please be advised that you are about to open the payment page where the price adjustment is waived for your customer.

                  <template v-if="domain === 'blueyonder'">
                    By continuing, the amount processed with this transaction will <span class="font-weight-black d-inline">incur a {{ waivedFeeRate }}% fee</span>.

                    This will be billed to you, the merchant, at the end of each month.
                  </template>
                </div>
                <template v-if="accountSelected.manager_password && !hasPermission('can_waive_without_password')">
                  <div class="pa-2">Please enter the password to waive the price adjustment fee:</div>
                  <v-text-field
                    label="Password"
                    v-model="waivePassword"
                    :error-messages="waivePasswordError"
                    append-icon="lock"
                    box
                    type="password"
                  ></v-text-field>
                </template>
              </template>
            </v-layout>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="checkWaivePassword">Confirm</v-btn>
            <v-btn @click="cancelWaiveModal">Cancel</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rerunModal" maxWidth="500px" @keydown.esc.prevent="closeRerunModal()" @input="v => v || closeRerunModal()">
      <v-card>
        <v-card-text class="text-xs-center" fill-height>
          <v-layout row wrap align-center>
            Press the process button below to run the previous transaction without the price adjustment fee
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmPaymentPlanModal" maxWidth="500px" persistent>
      <v-toolbar dense>
        <v-toolbar-title>Confirm Payment Schedule</v-toolbar-title>
      </v-toolbar>

      <v-card>
        <v-card-text>
          <v-container fluid grid-list-lg fill-height>
            <v-layout row wrap align-center>

              <template v-if="this.$route.query.hasOwnProperty('i')">
                <v-flex xs4 class="primary--text">Total Amount:</v-flex>
                <v-flex xs8>${{this.recurringTotalAmountFormatted}}</v-flex>
              </template>

              <template v-if="this.fields.amount && this.$route.query.hasOwnProperty('i')">
                <v-flex xs4 class="primary--text"><strong>Downpayment:</strong></v-flex>
                <v-flex xs8>
                  <strong>${{this.amountTotalFormatted}}</strong> <template v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !fields.waive_fee">*</template>
                  <v-chip color="primary" small outline class="pa-0 font-weight-bold">PAYABLE TODAY</v-chip>
                </v-flex>
              </template>

              <template v-if="!this.$route.query.hasOwnProperty('i')">
                <v-flex xs4 class="primary--text">Recurring Amount:</v-flex>
                <v-flex xs8>${{this.amountTotalFormatted}} *</v-flex>
              </template>

              <v-flex xs4 class="primary--text">Payment Frequency:</v-flex>
              <v-flex xs8>{{capitalize(this.fields.recurring)}}</v-flex>

              <template v-if="!this.fields.recurring_ongoing">
                <v-flex xs4 class="primary--text">No. of Payments:</v-flex>
                <v-flex xs8>{{this.fields.recurring_payments}}</v-flex>
              </template>

              <v-flex xs4 class="primary--text">Start Date:</v-flex>
              <v-flex xs8>{{date(this.fields.recurring_start_date)}}</v-flex>

              <template v-if="this.$route.query.hasOwnProperty('i')">
                <v-flex xs4 class="primary--text">Installment:</v-flex>
                <v-flex xs8>${{this.recurringAmountTotalPerPeriod}} <template v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !fields.waive_fee">*</template> x {{ !this.fields.recurring_last_amount ? this.fields.recurring_payments : (this.fields.recurring_payments - 1) }}</v-flex>
              </template>

              <template v-if="this.$route.query.hasOwnProperty('i') && this.fields.recurring_last_amount">
                <v-flex xs4 class="primary--text">Final Payment:</v-flex>
                <v-flex xs8>${{this.recurringLastAmountTotal}} <template v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !fields.waive_fee">*</template></v-flex>
              </template>

              <template v-if="!this.$route.query.hasOwnProperty('i')">
                <v-flex xs4 class="primary--text">Ongoing:</v-flex>
                <v-flex xs8>{{this.fields.recurring_ongoing ? 'Yes' : 'No'}}</v-flex>
              </template>

              <template v-if="!this.fields.recurring_ongoing">
                <v-flex xs4 class="primary--text">End Date:</v-flex>
                <v-flex xs8>{{date(this.fields.recurring_end_date)}}</v-flex>
              </template>

              <template v-if="$route.name === 'ChargeCard' && selectedTerminal.is_cashdiscount && !fields.waive_fee">
                <v-flex xs12 v-if="this.fields.amount && this.$route.query.hasOwnProperty('i')">** Price Adjustment Fees have been added to the Downpayment and the Installment payments.</v-flex>
                <v-flex xs12 v-else-if="this.$route.query.hasOwnProperty('i')">** Price Adjustment Fee has been added to the Installment payments.</v-flex>
                <v-flex xs12 v-else>** Price Adjustment Fee has been added to the Recurring payments.</v-flex>
              </template>

            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-container fluid grid-list-lg>
            <v-layout row wrap align-center>
              <v-flex xs3><v-btn color="primary" @click="closeConfirmPaymentPlanModal(true)">Confirm</v-btn></v-flex>
              <v-flex xs9>Confirm payment schedule & continue</v-flex>
              <v-flex xs3><v-btn color="error" @click="closeConfirmPaymentPlanModal()">Stop</v-btn></v-flex>
              <v-flex xs9>Go BACK to make changes</v-flex>
            </v-layout>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resultModal" maxWidth="500px" @keydown.esc.prevent="closeResult()" @input="v => v || closeResult()" :transition="false">
      <v-toolbar dense>
        <v-toolbar-title><v-icon v-if="isResponseError">fa-exclamation-triangle</v-icon><v-icon v-if="!isResponseError">fa-check</v-icon> {{ isResponseError ? 'Error' : 'Success' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click="closeResult">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text class="text-xs-center">
          <h3 v-if="isResponseError">{{ responseMessage }}</h3>

          <h4 class="primary--text" v-if="isResponseError && responseMessageDetails">{{ responseMessageDetails }}</h4>

          <h3 v-if="!isResponseError">
            <template v-if="Object.keys(transactionDetails).length && !isNaN(transactionDetails.total_amount)">
              The payment for <span class="title primary--text">${{ (transactionDetails.total_amount / 100).toFixed(2) }}</span> has been <span class="title primary--text" v-if="$route.name === 'ChargeCard'">approved</span><span
            class="title primary--text" v-if="$route.name === 'ChargeACH'">accepted</span>.
            </template>

            <div v-if="responseMessageDetails">{{ responseMessageDetails }}.</div>

            <div v-if="$route.name === 'ChargeACH' && this.fields.ach_authorization === 'email'"><br />Authorization email sent to <span class="title primary--text">{{ fields.billing_email }}</span>.</div>
          </h3>

        </v-card-text>
        <v-card-actions v-if="!isResponseError">
          <v-container fluid grid-list-lg ma-0 pa-0>
            <v-layout wrap justify-center>
              <v-flex xs6 class="text-xs-center" v-if="($route.name === 'ChargeCard' || this.fields.ach_authorization !== 'email') && Object.keys(this.transactionDetails).length">
                <v-btn @click="printReceipt">Print Receipt</v-btn>
              </v-flex>

              <v-flex xs6 class="text-xs-center" v-if="fields.billing_email && ($route.name === 'ChargeCard' || this.fields.ach_authorization !== 'email') && Object.keys(this.transactionDetails).length">
                <v-btn @click="emailReceipt">Email Receipt</v-btn>
              </v-flex>

              <v-flex xs6 class="text-xs-center" v-if="$route.query.hasOwnProperty('i') && Object.keys(this.recurringTransactionDetails).length">
                <v-btn @click="printSchedule">Print Schedule</v-btn>
              </v-flex>

              <v-flex xs6 class="text-xs-center" v-if="$route.query.hasOwnProperty('i') && fields.billing_email && Object.keys(this.recurringTransactionDetails).length">
                <v-btn @click="emailSchedule">Email Schedule</v-btn>
              </v-flex>

              <v-flex xs6 class="text-xs-center" v-if="$route.name === 'ChargeACH' && fields.ach_authorization.startsWith('print')">
                <v-btn @click="printAuth">Print ACH Auth</v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmModal
      v-model="showConfirmationModal"
      :amount="priceAdjustmentFee.total_amount"
      :price-adjustment="priceAdjustmentFee.cash_discount"
      :total="priceAdjustmentFee.total_amount_with_cash_discount"
      @confirmed="process"
    ></ConfirmModal>

    <ReceiptPrint ref="receipt" :transaction="transactionDetails"></ReceiptPrint>
    <AchAuthPrint ref="achAuth" :transaction="transactionDetails"></AchAuthPrint>
    <SchedulePrint ref="schedule" :transaction="recurringTransactionDetails" :downpayment="{ total_amount: (amountTotalFormatted * 100) }" :final="{ total_amount: (recurringLastAmountTotal * 100), future_post: fields.recurring_end_date }" :paymentcount= "fields.installments_total_count" :custom="{ total_amount: (fields.recurring_total_amount * 100) }"></SchedulePrint>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'
import APIMixin from '@/mixins/api'
import AssociatedMixin from '@/mixins/associated'
import AuthMixin from '@/mixins/auth'
import CashDiscountMixin from '@/mixins/cashDiscount'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'
import RecurringMixin from '@/mixins/recurring'
import UtilityMixin from '@/mixins/utility'
import ValidationMixin from '@/mixins/validation'

import AccountTerminalDebug from './components/AccountTerminalDebug.vue'
import AchAuthPrint from '@/components/transactions/AchAuthPrint.vue'
import AchFindInfoModal from '@/components/help/AchFindInfoDialog.vue'
import AmountThreshold from '@/components/terminal/AmountThreshold.vue'
import CountryField from '@/components/forms/CountryDropdown.vue'
import Lightbulb from '@/components/help/Lightbulb'
import ReceiptPrint from '@/components/transactions/ReceiptPrint.vue'
import SchedulePrint from '@/components/plans/SchedulePrint.vue'
import StateField from '@/components/forms/StateDropdown.vue'
import TerminalSelect from '@/components/terminal/components/TerminalSelect.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'

export default {
  components: {
    AccountTerminalDebug,
    AchAuthPrint,
    AchFindInfoModal,
    AmountThreshold,
    CountryField,
    Lightbulb,
    ReceiptPrint,
    SchedulePrint,
    StateField,
    TerminalSelect,
    ConfirmModal
  },

  mixins: [
    APIMixin,
    AssociatedMixin,
    AuthMixin,
    CashDiscountMixin,
    DisplayMixin,
    StoreMixin,
    RecurringMixin,
    UtilityMixin,
    ValidationMixin
  ],

  data: function () {
    return {
      responses: [],
      panel: [false],

      isProcessing: false,
      responseMessage: '',
      responseMessageDetails: '',
      isResponseError: false,

      closeDateModal: false,
      startDateModal: false,
      endDateModal: false,

      canWaive: false,
      waiveLevel: 0,
      waivePrevious: false,
      waiveHealthCard: false,
      waiveMessage: '',

      waiveHSAConfirmationModal: false,
      waiveConfirmationModal: false,
      waivePasswordPrompt: false,
      waivePassword: '',
      waivePasswordError: [],

      showPriceAdjustmentFee: false,
      showRecurringPriceAdjustmentFee: false,
      showRecurringLastAmountPriceAdjustmentFee: false,

      rerunModal: false,
      confirmPaymentPlanModal: false,
      recurringLocked: false,
      resultModal: false,
      receiptModal: false,
      recurringParentModal: false,
      recurringReceiptModal: false,
      showConfirmationModal: false,

      updateCardInfo: false,
      newPaymentInfo: false,
      parentId: 0,
      parentDetails: {},
      parentCustomDetails: {},
      transactionId: 0,
      transactionDetails: {},
      recurringTransactionDetails: {},
      submitNonce: '',

      achTypes: [
        {value: 'CHECKING', text: 'Personal checking'},
        {value: 'SAVINGS', text: 'Personal savings'},
        {value: 'BUSINESSCK', text: 'Business checking'}
      ],

      recurringTypes: [
        {value: 'none', text: 'None'},
        {value: 'daily', text: 'Every day'},
        {value: 'weekly', text: 'Every week'},
        {value: 'biweekly', text: 'Every 2 weeks'},
        {value: 'monthly', text: 'Every month'},
        {value: 'bimonthly', text: 'Every 2 months'},
        {value: 'quarterly', text: 'Every 4 months'},
        {value: 'semiannually', text: 'Every 6 months'},
        {value: 'annually', text: 'Every 12 months'}
      ],

      achAuthOptions: [
        {value: 'none', text: 'None'},
        {value: 'onfile', text: 'Authorization on file'},
        {value: 'printsign', text: 'Print and sign now'},
        {value: 'email', text: 'Send authorization email'}
      ],

      terminalRules: [
        (v) => !!v || 'Terminal is required'
      ],
      nameRules: [
        (v) => !!v || 'Name is required'
      ],
      cardRules: [
        (v) => !!v || 'Card number is required',
        (v) => this.validateCardNumber(v) || 'Card number is invalid'
      ],
      cvvRules: [
        (v) => !!v || 'CVV is required'
      ],
      expirationRules: [
        (v) => !!v || 'Expiration is required'
      ],
      routingRules: [
        (v) => !!v || 'Routing number is required',
        (v) => v.length === 9 || 'Routing number must be nine digits',
        (v) => this.validateABA(v) || 'Routing number is invalid'
      ],
      accountRules: [
        (v) => !!v || 'Account number is required',
        (v) => (v.length < 18) || 'Account number must be less than 18 digits'
        // (v) => (v.toString() === this.fields.ach_account_number_verify) || 'Account number does not match verification'
      ],
      accountVerifyRules: [
        (v) => !!v || 'Account number verification is required',
        (v) => (v.length < 18) || 'Account number verification must be less than 18 digits',
        (v) => (v.toString() === this.fields.ach_account_number) || 'Account number verification does not match'
      ],
      amountRules: [
        (v) => !!v || 'Amount is required',
        (v) => !isNaN(parseFloat(v.replace(/[^0-9.-]+/g, ''))) || 'Amount must be be numeric',
        (v) => parseFloat(v.replace(/[^0-9.-]+/g, '')) >= 0 || 'Amount must be a positive amount',
        // (v) => (!this.amountThresholdAllow && Number(v.replace(/[^0-9.-]+/g, '')) < this.amountThreshold) || `Verify amount over $${this.amountThreshold}.00`,
        (v) => (this.hasPermission('can_bypass_transaction_limit') || parseFloat(v.replace(/[^0-9.-]+/g, '')) <= 500000) || 'Amount must be $500,000 or less'
      ],
      downpaymentRules: [
        (v) => !isNaN(parseFloat(v.replace(/[^0-9.-]+/g, ''))) || 'Downpayment must be numeric',
        (v) => parseFloat(v.replace(/[^0-9.-]+/g, '')) >= 0 || 'Downpayment must be a positive amount',
        (v) => (this.hasPermission('can_bypass_transaction_limit') || parseFloat(v.replace(/[^0-9.-]+/g, '')) <= 500000) || 'Downpayment must be $500,000 or less',
        (v) => parseFloat(v.replace(/[^0-9.-]+/g, '')) < this.fields.recurring_total_amount || 'Downpayment must be less than the total amount'
      ],
      recurringAmountRules: [
        (v) => !isNaN(parseFloat(v.replace(/[^0-9.-]+/g, ''))) || 'Installment amount must be numeric',
        (v) => parseFloat(v.replace(/[^0-9.-]+/g, '')) >= 0 || 'Installment amount must be a positive amount',
        (v) => (this.hasPermission('can_bypass_transaction_limit') || parseFloat(v.replace(/[^0-9.-]+/g, '')) <= 500000) || 'Installment amount must be $500,000 or less',
        (v) => parseFloat(v.replace(/[^0-9.-]+/g, '')) <= this.recurringRemainingBalance || 'Installment amount must be equal to or less than the remaining balance'
      ],
      feeRules: [
        (v) => !isNaN(v) || 'Fee needs to be numeric'
      ],
      recurringFrequencyRules: [
        (v) => (!!v && v !== 'none') || 'Recurring frequency required'
      ],
      recurringStartRules: [
        // required if recurring
        // today or later
        (v) => (!!v && v !== '') || 'Recurring start date required'
      ],
      recurringEndRules: [
        // required if recurring
        // day after start date or later
        (v) => (!!v && v !== '' && !this.fields.recurring_ongoing) || 'Recurring end date required'
      ],
      addressRules: [
        (v) => !!v || 'Address is required'
      ],
      cityRules: [
        (v) => !!v || 'City is required'
      ],
      stateRules: [
        (v) => !!v || 'State is required'
      ],
      postalcodeRules: [
        (v) => !!v || 'Postal code is required'
      ],
      achAuthRules: [
        (v) => (!!v && v !== 'none') || 'Authorization type is required'
      ],
      emailRules: [
        (v) => !v || /.+@.+/.test(v) || 'Email must be valid'
      ],
      passwordRules: [
        (v) => !!v || 'Password is required'
      ],

      current_date: Moment().format('YYYY-MM-DD'),

      selectedTerminal: {},

      fields: this.defaultFields()
    }
  },

  props: {
    paymentType: String
  },

  async created () {
    if (!this.hasPermission('can_use_virtual_terminal')) {
      this.$store.commit('error', 'Access Denied')
      this.$router.go(-1)
    }
  },

  destroyed () {
    this.$store.commit('clearErrors', 'timed-out-error-mesasge')
    this.$store.commit('clearInfo')
    this.$store.commit('clearSuccesses')
  },

  mounted () {
    this.reset()
  },

  computed: {
    installmentProcessorFilter () {
      if (!this.$route.query.hasOwnProperty('i')) {
        return 0
      }

      const cardHasFaps = this.terminalsCardProcessors.includes(3)
      const cardHasPnx = this.terminalsCardProcessors.includes(6)
      const achHasFaps = this.terminalsACHProcessors.includes(3)
      const achHasPnx = this.terminalsACHProcessors.includes(6)

      if (this.$route.name === 'ChargeCard') {
        if (cardHasFaps && cardHasPnx) return [3, 6]
        if (cardHasPnx) return 6
        if (cardHasFaps) return 3
        return 0
      }
      if (this.$route.name === 'ChargeACH') {
        if (achHasFaps && achHasPnx) return [3, 6]
        if (achHasPnx) return 6
        if (achHasFaps) return 3
        return 0
      }
      return 0
    },

    isPayNetworx () {
      return this.selectedTerminal && this.selectedTerminal.processor_id === 6
    },

    parentCardNumber () {
      return this.parentDetails.card_bin + ' ****** ' + this.parentDetails.last_four
    },

    parentExpiration () {
      const exp = this.parentDetails.card_expiration || this.parentDetails.card_expiration_yymm
      if (!exp || String(exp).length < 4) {
        return ''
      }
      const value = String(exp)
      return value.slice(-2) + ' / ' + value.slice(0, 2)
    },

    parentAccountNumber () {
      return '**********' + this.parentDetails.last_four
    },

    cardMask () {
      if (this.cardType === 'amex') {
        return '#### ###### #####'
      } else if (this.cardType === 'diners') {
        return '#### ###### #####'
      } else if (this.cardType === 'discover') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb15') {
        return '#### ###### #####'
      } else if (this.cardType === 'maestro') {
        return '#### #### #### ####'
      } else if (this.cardType === 'mastercard') {
        return '#### #### #### ####'
      } else if (this.cardType === 'unionpay') {
        return '#### #### #### ####'
      } else if (this.cardType === 'visa') {
        return '#### #### #### ####'
      }
      return '#### #### #### ####'
    },

    cvvMask () {
      if (this.cardType === 'amex' || this.cardType === 'diners' || this.cardType === 'jcb15') {
        return '####'
      }
      return '###'
    },

    cardType () {
      if (this.parentDetails.payment_type) {
        return this.parentDetails.payment_type
      }

      const number = this.fields.card_number || ''

      let amex = /^3[47]\d{0,13}/
      let diners = /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/
      let discover = /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/
      let jcb = /^(?:35\d{0,2})\d{0,12}/
      let jcb15 = /^(?:2131|1800)\d{0,11}/
      let maestro = /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/
      let mastercard = /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/
      let unionpay = /^62\d{0,14}/
      let visa = /^4\d{0,15}/

      if (amex.test(number)) {
        return 'amex'
      } else if (diners.test(number)) {
        return 'diners'
      } else if (discover.test(number)) {
        return 'discover'
      } else if (jcb.test(number)) {
        return 'jcb'
      } else if (jcb15.test(number)) {
        return 'jcb15'
      } else if (maestro.test(number)) {
        return 'maestro'
      } else if (mastercard.test(number)) {
        return 'mastercard'
      } else if (unionpay.test(number)) {
        return 'unionpay'
      } else if (visa.test(number)) {
        return 'visa'
      }
      return 'unknown'
    },

    amount () {
      return this.fields.amount
    },

    amountTotalFormatted () {
      if (isNaN(this.fields.amount) || this.fields.amount === 0 || this.fields.amount === null) {
        return ''
      }

      return (Number(this.fields.amount) + Number(this.calculateCashDiscountFee(this.fields.amount))).toFixed(2)
    },

    priceAdjustmentFee: function () {
      return {
        total_amount: this.fields.amount,
        cash_discount: this.calculateCashDiscountFee(this.fields.amount),
        total_amount_with_cash_discount: this.fields.amount + (parseFloat(this.calculateCashDiscountFee(this.fields.amount)))
      }
    },

    waive () {
      return this.fields.waive_fee
    },

    formattedCloseDate () {
      return this.fields.close_date ? Moment(this.fields.close_date).format('MMM-DD-YYYY') : ''
    },

    formattedRecurringStartDate () {
      return this.fields.recurring_start_date ? Moment(this.fields.recurring_start_date).format('MMM-DD-YYYY') : ''
    },

    formattedRecurringEndDate () {
      return this.fields.recurring_end_date ? Moment(this.fields.recurring_end_date).format('MMM-DD-YYYY') : ''
    },

    minimumCloseDate: function () {
      return Moment().add(1, 'days').format('YYYY-MM-DD')
    },

    minimumRecurringStartDate: function () {
      return Moment().format('YYYY-MM-DD')
    },

    minimumRecurringEndDate: function () {
      return Moment(this.fields.recurring_start_date, 'YYYY-MM-DD').add(2, 'days').format('YYYY-MM-DD')
    },

    recurring: function () {
      return this.fields.recurring
    },

    recurringEndDate: function () {
      return this.fields.recurring_end_date
    },

    recurringOngoing: function () {
      return this.fields.recurring_ongoing
    },

    recurringTotalAmountFormatted () {
      if (isNaN(this.fields.recurring_total_amount) || this.fields.recurring_total_amount === null) {
        return '0.00'
      }

      return Number(this.fields.recurring_total_amount).toFixed(2)
    },

    recurringRemainingBalance: function () {
      let subtotal = this.fields.recurring_total_amount - this.fields.amount

      if (isNaN(subtotal)) {
        return ''
      }

      return Number(subtotal).toFixed(2)
    },

    recurringAmountPerPeriod: function () {
      let subtotal = (this.fields.recurring_total_amount - this.fields.amount) / this.fields.recurring_payments

      if (isNaN(subtotal) || subtotal === 0 || subtotal === Infinity) {
        return ''
      }

      return Number(subtotal).toFixed(2)
    },

    recurringPriceAdjustmentFee: function () {
      if (this.fields.waive_fee) {
        return ''
      }

      if (isNaN(this.fields.recurring_amount) || this.fields.recurring_amount === 0 || this.fields.recurring_amount === 0.00) {
        return ''
      }

      return this.calculateCashDiscountFee(this.fields.recurring_amount)
    },

    recurringAmountTotalPerPeriod: function () {
      if (isNaN(this.fields.recurring_amount) || this.fields.recurring_amount === 0 || this.fields.recurring_amount === 0.00) {
        return ''
      }

      return (Number(this.fields.recurring_amount) + Number(this.calculateCashDiscountFee(this.fields.recurring_amount))).toFixed(2)
    },

    recurringLastAmountPriceAdjustmentFee: function () {
      if (this.fields.waive_fee) {
        return ''
      }

      if (isNaN(this.fields.recurring_last_amount) || this.fields.recurring_last_amount === 0 || this.fields.recurring_last_amount === 0.00) {
        return ''
      }

      return this.calculateCashDiscountFee(this.fields.recurring_last_amount)
    },

    recurringLastAmountTotal: function () {
      if (isNaN(this.fields.recurring_last_amount) || this.fields.recurring_last_amount === null) {
        return '0.00'
      }

      return (Number(this.fields.recurring_last_amount) + Number(this.calculateCashDiscountFee(this.fields.recurring_last_amount))).toFixed(2)
    },

    waivedFeeRate: function () {
      if (this.accountSelected.hasOwnProperty('permissions') && this.accountSelected.permissions !== null && this.accountSelected.permissions.hasOwnProperty('waived_fee_rate')) {
        return this.accountSelected.permissions.waived_fee_rate
      }

      if (this.domain === 'blueyonder') {
        return 3.2
      }

      if (this.selectedTerminal.hasOwnProperty('cashdiscount_schedule')) {
        return this.calculateRate(this.selectedTerminal)
      }

      return 0
    },

    waivedDebitFeeRate: function () {
      if (this.accountSelected.hasOwnProperty('permissions') && this.accountSelected.permissions !== null && this.accountSelected.permissions.hasOwnProperty('waived_debit_fee_rate')) {
        return this.accountSelected.permissions.waived_debit_fee_rate
      }

      return 2.6
    },

    waivedHSAFeeRate: function () {
      if (this.accountSelected.hasOwnProperty('permissions') && this.accountSelected.permissions !== null && this.accountSelected.permissions.hasOwnProperty('waived_hsa_fee_rate')) {
        return this.accountSelected.permissions.waived_hsa_fee_rate
      }
      return 2.95
    }
  },

  filters: {
    pretty (data) {
      return JSON.stringify(JSON.parse(JSON.stringify(data)), null, 2)
    },
    localeDate (data) {
      return new Date(data).toLocaleString()
    }
  },

  watch: {
    '$route' (to, from) {
      this.reset()
    },
    recurring (val) {
      this.setPaymentCount()
    },
    recurringStartDate (val) {
      this.setEndDate()
    },
    recurringEndDate (val) {
      this.setPaymentCount()
    },
    recurringOngoing () {
      this.setPaymentCount()
    }
  },

  methods: {
    defaultFields () {
      return {
        method: null,
        amount: 0.00,
        downpayment: 0.00,
        fee: 0.00,
        fee_waived: 0.00,
        fee_per_installment: 0.00,
        percentage: 0,
        token: null,

        card_name: '',
        card_number: '',
        card_cvv: null,
        card_expiration: '',

        close_date: null,
        recurring: 'none',
        recurring_start_date: Moment().format('YYYY-MM-DD'),
        recurring_end_date: '',
        recurring_payments: 0,
        recurring_ongoing: false,
        recurring_total_amount: 0.00,
        recurring_amount: 0.00,
        recurring_last_amount: 0.00,
        installments_total_count: 0,

        ach_routing_number: '',
        ach_account_number: '',
        ach_account_number_verify: '',
        ach_check_number: null,
        ach_type: 'CHECKING',
        ach_authorization: 'none',

        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_postalcode: '',
        billing_country: 'USA',
        billing_phone: null,
        billing_email: null,
        customer_po: null,

        custom_field_1: null,
        custom_field_2: null,
        custom_field_3: null,
        notes: null,
        tokenize: true,
        waive_fee: false
      }
    },

    getRequiredField (field) {
      if (!this.fields.hasOwnProperty(field)) {
        return 'Required'
      }

      if (field === 'ach_authorization') {
        if (this.fields[field] && this.fields[field] !== 'none') {
          return ''
        }
      } else if (this.fields[field]) {
        return ''
      }

      return 'Required'
    },

    async validateBeforeProcessing () {
      if (!this.$refs.form1.validate() || !this.$refs.form2.validate()) {
        let errors = []
        this.$refs.form1.inputs.forEach(e => {
          if (e.hasError) {
            errors.push(e.errorBucket[0])
          }
        })
        this.$refs.form2.inputs.forEach(e => {
          if (e.hasError) {
            errors.push(e.errorBucket[0])
          }
        })

        let message = 'Validation Error'

        if (errors.length > 1) {
          message = 'Validation Errors'
        }

        Bus.$emit('openModal', {
          status: 'error',
          message: message,
          messageDetails: errors
        })

        return false
      } else {
        this.showConfirmModal()
      }
    },

    async process () {
      try {
        let options = { ...this.fields }

        if (!this.selectedTerminal.terminal_id) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Please select a terminal'
          })

          return false
        }
        options.terminal_id = this.selectedTerminal.terminal_id

        this.isProcessing = true
        Bus.$emit('openWaitModal')

        options.recurring_offset = new Date().getTimezoneOffset() / 60

        if (this.parentId && this.parentDetails.token) {
          options.token = this.parentDetails.token
        }

        if (this.fields.recurring_ongoing) {
          options.recurring_end_date = '2099-12-31'
        }

        if (this.$route.name === 'ChargeCard') {
          delete options.ach_routing_number
          delete options.ach_account_number
          delete options.ach_account_number_verify
          delete options.ach_check_number
          delete options.ach_type
          delete options.ach_authorization
          options.request_type = 'sale'
          options.card_month = options.card_expiration.slice(0, 2)
          options.card_year = options.card_expiration.slice(-2)
          options.health_rewards_card = this.waiveHealthCard
          options.debit_card = this.waiveLevel === 3
          options.fee = this.calculateCashDiscountFee(options.amount)
        } else if (this.$route.name === 'ChargeACH') {
          if (options.ach_authorization === 'email' && !options.billing_email) {
            Bus.$emit('openModal', {
              status: 'error',
              message: 'Email required for ACH authorization'
            })
            return false
          }

          delete options.card_number
          delete options.card_cvv
          delete options.card_expiration
          delete options.fee
          options.request_type = 'ach'
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Cannot determine payment type'
          })
          return false
        }

        if (!options.card_name && this.parentDetails.billing_name) {
          options.card_name = this.parentDetails.billing_name
        }

        // PayNetworx installments: create a schedule with installment flags
        // This is simpler than FPG - just one schedule creation with installment_count
        if (this.$route.query.hasOwnProperty('i') && this.isPayNetworx) {
          await this.processPaynetworxInstallment(options)
          return
        }

        // handle downpayment of installments and last payment
        let downpaymentOptions = {}
        let downpaymentResult
        let lastPaymentOptions = {}
        let lastPaymentResult

        let transactionGroupId = this.generateId(36)
        let installmentTotalCount = this.fields.installments_total_count = this.fields.recurring_payments

        // setup transaction group from parent
        // @TODO find all instances we want this value
        if (this.$route.query.hasOwnProperty('i') && this.$route.query.i === 2 && this.parentDetails.hasOwnProperty('transaction_group_custom_tracking_id') && this.parentDetails.transaction_group_custom_tracking_id) {
          transactionGroupId = this.parentDetails.transaction_group_custom_tracking_id
        }

        // process the downpayment first
        if (this.$route.query.hasOwnProperty('i') && options.amount > 0) {
          downpaymentOptions = { ...options }
          delete downpaymentOptions.recurring
          delete downpaymentOptions.recurring_start_date
          delete downpaymentOptions.recurring_end_date
          delete downpaymentOptions.recurring_offset
          downpaymentOptions.fee = this.calculateCashDiscountFee(downpaymentOptions.amount)
          downpaymentOptions.nonce = this.submitNonce

          downpaymentOptions.transaction_group_id = transactionGroupId
          downpaymentOptions.transaction_group_total_amount = downpaymentOptions.recurring_total_amount
          downpaymentOptions.transaction_group_total_count = installmentTotalCount

          downpaymentResult = await this.api({
            method: 'post',
            url: `/terminal/payment`,
            data: downpaymentOptions
          })

          if (downpaymentResult.data.response.ResponseText === 'APPROVED' || downpaymentResult.data.response.ResponseText === 'PENDING' || downpaymentResult.data.response.ResponseText === 'CHECK ACCEPTED') {
            this.transactionDetails = Object.assign({}, this.transactionDetails, downpaymentResult.data.response.Transaction)
            this.transactionDetails = downpaymentResult.data.response.Transaction

            if (downpaymentResult.data.response.ResponseText === 'APPROVED') {
              this.responseMessage = 'Payment Approved'
            } else if (downpaymentResult.data.response.ResponseText === 'PENDING') {
              this.responseMessage = 'Scheduled Payment Approved'
            } else if (downpaymentResult.data.response.ResponseText === 'CHECK ACCEPTED') {
              this.responseMessage = 'Payment Accepted'
            }
          } else {
            let error = downpaymentResult.data.response.ResponseText
            if (downpaymentResult.data.response.ErrMsg) {
              error += ': ' + downpaymentResult.data.response.ErrMsg
            }
            this.transactionDetails = {}
            this.isResponseError = true
            this.responseMessage = error
            this.responseMessageDetails = ''
            this.isProcessing = false

            if (error === 'DECLINED' && this.$route.name === 'ChargeCard') {
              this.responseMessageDetails = 'Please verify card data including the expiration and CVV'
            }

            if (downpaymentResult.data.response.Transaction && downpaymentResult.data.response.Transaction.hasOwnProperty('error_messages') && downpaymentResult.data.response.Transaction.error_messages) {
              let messages = JSON.parse(downpaymentResult.data.response.Transaction.error_messages)

              // show detailed error as long as it isn't the generic 'do not honor'
              if (messages.length > 0 && messages[0] !== 'TRANS DENIED DO NOT HONOR' && messages[0] !== error) {
                this.responseMessageDetails = messages.join()
              }
            }

            this.resultModal = true
            this.submitNonce = this.generateId(36)
            return
          }

          this.responses.unshift(downpaymentResult.data)

          // set the recurring amount for the next transaction
          // options.amount = this.recurringAmountPerPeriod
          options.amount = this.fields.recurring_amount
          options.fee = this.calculateCashDiscountFee(options.amount)
        } else if (this.$route.query.hasOwnProperty('i')) {
          // set the recurring amount for the next transaction
          // options.amount = this.recurringAmountPerPeriod
          options.amount = this.fields.recurring_amount
          options.fee = this.calculateCashDiscountFee(this.fields.recurring_amount)
        }

        if (this.$route.query.hasOwnProperty('i')) {
          options.is_installment = 1
          options.transaction_group_id = transactionGroupId
          options.transaction_group_total_amount = options.recurring_total_amount
          options.transaction_group_total_count = installmentTotalCount
        }

        // account for last payment and moving the end date
        if (this.$route.query.hasOwnProperty('i') && options.recurring_last_amount) {
          lastPaymentOptions = { ...options }

          delete lastPaymentOptions.recurring
          delete lastPaymentOptions.recurring_start_date
          delete lastPaymentOptions.recurring_end_date
          delete lastPaymentOptions.recurring_offset

          lastPaymentOptions.nonce = this.submitNonce
          lastPaymentOptions.close_date = options.recurring_end_date
          lastPaymentOptions.amount = options.recurring_last_amount
          lastPaymentOptions.fee = this.calculateCashDiscountFee(options.recurring_last_amount)
          lastPaymentOptions.transaction_group_id = transactionGroupId
          lastPaymentOptions.transaction_group_total_amount = options.recurring_total_amount
          lastPaymentOptions.transaction_group_total_count = installmentTotalCount

          // set recurring end date back by one to account for the last payment
          // if recurring is a single payment we need to account for setting the end date forward slightly
          let previousEndDate = this.calculatePreviousRecurringDate(this.fields.recurring, this.fields.recurring_end_date)
          if (Moment(previousEndDate).isAfter(options.recurring_start_date)) {
            options.recurring_end_date = previousEndDate
          } else {
            options.recurring_end_date = this.fields.recurring_start_date
          }
        }

        // if rerunning, duplicate the transaction group
        if (this.$route.query.rerun && this.parentDetails.hasOwnProperty('transaction_group_custom_tracking_id') && this.parentDetails.transaction_group_custom_tracking_id) {
          options.transaction_group_id = this.parentDetails.transaction_group_custom_tracking_id
          options.transaction_group_total_amount = options.recurring_total_amount
          options.transaction_group_total_count = installmentTotalCount
        }

        options.nonce = this.submitNonce

        let result = await this.api({
          method: 'post',
          url: `/terminal/payment`,
          data: options
        })

        // process the last payment
        if (this.$route.query.hasOwnProperty('i') && options.recurring_last_amount) {
          lastPaymentResult = await this.api({
            method: 'post',
            url: `/terminal/payment`,
            data: lastPaymentOptions
          })

          if (lastPaymentResult.data.response.ResponseText === 'APPROVED' || lastPaymentResult.data.response.ResponseText === 'PENDING' || lastPaymentResult.data.response.ResponseText === 'CHECK ACCEPTED') {
            if (lastPaymentResult.data.response.ResponseText === 'APPROVED') {
              this.responseMessage = 'Payment Approved'
            } else if (lastPaymentResult.data.response.ResponseText === 'PENDING') {
              this.responseMessage = 'Scheduled Payment Approved'
            } else if (lastPaymentResult.data.response.ResponseText === 'CHECK ACCEPTED') {
              this.responseMessage = 'Payment Accepted'
            }
          } else {
            let error = lastPaymentResult.data.response.ResponseText
            if (lastPaymentResult.data.response.ErrMsg) {
              error += ': ' + lastPaymentResult.data.response.ErrMsg
            }
            this.transactionDetails = {}
            this.isResponseError = true
            this.responseMessage = error
            this.responseMessageDetails = ''
            this.isProcessing = false

            if (error === 'DECLINED' && this.$route.name === 'ChargeCard') {
              this.responseMessageDetails = 'Please verify card data including the expiration and CVV'
            }

            if (lastPaymentResult.data.response.Transaction && lastPaymentResult.data.response.Transaction.hasOwnProperty('error_messages') && lastPaymentResult.data.response.Transaction.error_messages) {
              let messages = JSON.parse(lastPaymentResult.data.response.Transaction.error_messages)

              // show detailed error as long as it isn't the generic 'do not honor'
              if (messages.length > 0 && messages[0] !== 'TRANS DENIED DO NOT HONOR' && messages[0] !== error) {
                this.responseMessageDetails = messages.join()
              }
            }

            this.resultModal = true
            this.submitNonce = this.generateId(36)
            return
          }

          this.responses.unshift(lastPaymentResult.data)
        }

        if (result.data.response.ResponseText === 'APPROVED') {
          this.recurringTransactionDetails = Object.assign({}, result.data.response.Transaction)
          if (this.$route.query.hasOwnProperty('i')) {
            this.responseMessageDetails = 'Installment plan approved'
          } else {
            this.transactionDetails = result.data.response.Transaction
            this.responseMessage = 'Payment Approved'
          }
        } else if (result.data.response.ResponseText === 'PENDING') {
          this.recurringTransactionDetails = Object.assign({}, result.data.response.Transaction)
          if (this.$route.query.hasOwnProperty('i')) {
            this.responseMessageDetails = 'Installment plan approved'
          } else {
            this.transactionDetails = result.data.response.Transaction
            this.responseMessage = 'Scheduled Payment Approved'
          }
        } else if (result.data.response.ResponseText === 'CHECK ACCEPTED') {
          this.recurringTransactionDetails = Object.assign({}, result.data.response.Transaction)
          if (this.$route.query.hasOwnProperty('i')) {
            this.responseMessageDetails = 'Installment plan accepted'
          } else {
            this.transactionDetails = result.data.response.Transaction
            this.responseMessage = 'Payment Accepted'
          }
        } else {
          let error = result.data.response.ResponseText
          if (result.data.response.ErrMsg) {
            error += ': ' + result.data.response.ErrMsg
          }
          this.transactionDetails = {}
          this.recurringTransactionDetails = {}
          this.isResponseError = true
          this.responseMessage = error
          this.responseMessageDetails = ''
          this.isProcessing = false

          if (error === 'DECLINED' && this.$route.name === 'ChargeCard') {
            this.responseMessageDetails = 'Please verify card data including the expiration and CVV'
          }

          if (result.data.response.Transaction && result.data.response.Transaction.hasOwnProperty('error_messages') && result.data.response.Transaction.error_messages) {
            let messages = JSON.parse(result.data.response.Transaction.error_messages)

            // show detailed error as long as it isn't the generic 'do not honor'
            if (messages.length > 0 && messages[0] !== 'TRANS DENIED DO NOT HONOR' && messages[0] !== error) {
              this.responseMessageDetails = messages.join()
            }
          }

          this.submitNonce = this.generateId(36)
        }

        if ((result.data.response.ResponseText === 'APPROVED' ||
          result.data.response.ResponseText === 'PENDING' ||
          result.data.response.ResponseText === 'CHECK ACCEPTED') &&
          (!this.parentDetails.hasOwnProperty('settled') ||
          (this.parentDetails.hasOwnProperty('settled') &&
          !this.parentDetails.settled)) &&
          this.$route.query.rerun) {
          // void / cancel recurring
          let options = {
            terminal_id: this.parentDetails.terminal_id,
            transaction_id: this.parentDetails.id
          }

          // if rerunning, duplicate the transaction group
          if (this.parentDetails.hasOwnProperty('transaction_group_custom_tracking_id') && this.parentDetails.transaction_group_custom_tracking_id) {
            options.transaction_group_id = this.parentDetails.transaction_group_custom_tracking_id
            options.transaction_group_total_amount = options.recurring_total_amount
            options.transaction_group_total_count = installmentTotalCount
          }

          let result = await this.api({
            method: 'post',
            url: `/terminal/void`,
            data: options
          })

          if (result.data) {
            if (result.data.response.ActionCode === '000') {
              if (this.responseMessageDetails) {
                this.responseMessageDetails += '. '
              }
              this.responseMessageDetails += 'Original transaction cancelled'
            } else {
              if (this.responseMessageDetails) {
                this.responseMessageDetails += '. '
              }
              this.responseMessageDetails += 'Problem cancelling original transaction'
            }
          } else {
            if (this.responseMessageDetails) {
              this.responseMessageDetails += '. '
            }
            this.responseMessageDetails += 'Problem cancelling original transaction'
          }
        }

        this.resultModal = true
        this.responses.unshift(result.data)
        this.panel = [true]
      } catch (err) {
        this.submitNonce = this.generateId(36)
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.isProcessing = false
        Bus.$emit('closeWaitModal')
      }
    },

    async processPaynetworxInstallment (baseOptions) {
      try {
        let options = { ...baseOptions }
        const installmentCount = parseInt(this.fields.recurring_payments)

        options.is_installment = 1
        options.installment_count = installmentCount
        options.recurring = this.fields.recurring
        options.recurring_start_date = this.fields.recurring_start_date
        options.recurring_end_date = this.fields.recurring_end_date
        options.recurring_notify = this.fields.recurring_notify ? 1 : 0

        options.amount = this.fields.recurring_amount
        options.fee = this.calculateCashDiscountFee(options.amount)
        options.installment_total_amount = this.fields.recurring_total_amount

        options.nonce = this.submitNonce + '_schedule'
        const result = await this.api({
          method: 'post',
          url: `/terminal/payment`,
          data: options
        })

        if (result.data.response.ResponseText === 'APPROVED' ||
            result.data.response.ResponseText === 'PENDING' ||
            result.data.response.ResponseText === 'CHECK ACCEPTED') {
          this.recurringTransactionDetails = Object.assign({}, result.data.response.Transaction)
          this.fields.installments_total_count = installmentCount
          this.responseMessageDetails = 'Installment schedule created'
          if (!this.responseMessage) {
            this.responseMessage = 'Payment Approved'
          }
        } else {
          let error = result.data.response.ResponseText
          if (result.data.response.ErrMsg) {
            error += ': ' + result.data.response.ErrMsg
          }
          this.recurringTransactionDetails = {}
          this.isResponseError = true
          this.responseMessage = 'Schedule creation failed: ' + error
          this.responseMessageDetails = ''
          this.resultModal = true
          this.responses.unshift(result.data)
          this.panel = [true]
          return
        }

        let scheduleTrackingId = result.data.response.ScheduleTrackingId

        if (this.fields.amount && parseFloat(this.fields.amount) > 0) {
          const downpaymentOptions = {
            ...options,
            amount: this.fields.amount,
            fee: this.calculateCashDiscountFee(this.fields.amount),
            nonce: this.submitNonce
          }
          delete downpaymentOptions.recurring
          delete downpaymentOptions.recurring_start_date
          delete downpaymentOptions.recurring_end_date
          delete downpaymentOptions.is_installment
          delete downpaymentOptions.installment_count

          if (scheduleTrackingId) {
            downpaymentOptions.schedule_tracking_id = scheduleTrackingId
          }

          const downpaymentResult = await this.api({
            method: 'post',
            url: `/terminal/payment`,
            data: downpaymentOptions
          })

          if (downpaymentResult.data.response.ResponseText !== 'APPROVED' &&
              downpaymentResult.data.response.ResponseText !== 'PENDING' &&
              downpaymentResult.data.response.ResponseText !== 'CHECK ACCEPTED') {
            let error = downpaymentResult.data.response.ResponseText
            if (downpaymentResult.data.response.ErrMsg) {
              error += ': ' + downpaymentResult.data.response.ErrMsg
            }

            if (scheduleTrackingId) {
              try {
                await this.api({
                  method: 'delete',
                  url: `/schedules/${scheduleTrackingId}`
                })
              } catch (cancelErr) {
                console.error('Failed to cancel schedule after downpayment failure:', cancelErr)
                error = 'Downpayment failed and schedule cancellation also failed. Contact support immediately to prevent unwanted charges. Schedule ID: ' + scheduleTrackingId
              }
            }

            this.transactionDetails = {}
            this.isResponseError = true
            this.responseMessage = 'Downpayment failed: ' + error
            this.responseMessageDetails = ''
            this.resultModal = true
            this.submitNonce = this.generateId(36)
            return
          }

          this.transactionDetails = downpaymentResult.data.response.Transaction
          this.responseMessage = 'Downpayment Approved'
          this.responseMessageDetails = 'Installment schedule created'
          this.responses.unshift(downpaymentResult.data)
        }

        this.resultModal = true
        this.responses.unshift(result.data)
        this.panel = [true]
      } catch (err) {
        this.submitNonce = this.generateId(36)
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.isProcessing = false
        Bus.$emit('closeWaitModal')
      }
    },

    async reset (opts) {
      this.updateCardInfo = false
      this.newPaymentInfo = false
      this.waiveHSAConfirmationModal = false
      this.waiveConfirmationModal = false
      this.rerunModal = false
      this.confirmPaymentPlanModal = false
      this.recurringLocked = false
      this.resultModal = false
      this.receiptModal = false

      this.$refs.form1.resetValidation()
      this.$refs.form2.resetValidation()

      this.$store.commit('clearErrors')
      this.$store.commit('clearInfo')
      this.$store.commit('clearSuccesses')

      this.panel = [false]

      this.isProcessing = false
      this.responseMessage = ''
      this.responseMessageDetails = ''
      this.isResponseError = false

      this.closeDateModal = false
      this.startDateModal = false
      this.endDateModal = false

      this.parentId = 0
      this.parentDetails = {}
      this.parentCustomDetails = {}
      this.transactionId = 0
      this.transactionDetails = {}
      this.recurringTransactionDetails = {}

      this.canWaive = false
      this.waiveLevel = 0
      this.waivePrevious = false
      this.waiveHealthCard = false

      this.selectedTerminal = {}
      this.$refs.terminal.reset()

      this.submitNonce = this.generateId(36)

      this.fields = this.defaultFields()

      // installment/recurring frequency options are limited
      if (this.$route.query.hasOwnProperty('i') || this.$route.query.hasOwnProperty('r')) {
        this.recurringTypes = [
          {value: 'none', text: 'None'},
          {value: 'daily', text: 'Every day'},
          {value: 'weekly', text: 'Every week'},
          {value: 'biweekly', text: 'Every 2 weeks'},
          {value: 'monthly', text: 'Every month'},
          {value: 'annually', text: 'Every 12 months'}
        ]
      }

      if (!this.$route.query.hasOwnProperty('r') && !this.$route.query.hasOwnProperty('i')) {
        this.recurringLocked = true
      }

      if (this.$route.query.hasOwnProperty('waive') && this.$route.query.waive === '1') {
        if (!this.hasPermission('can_waive_card_fees')) {
          this.$router.go(-1)
        } else {
          this.canWaive = true
          this.fields.waive_fee = true
          this.waiveLevel = 1
          this.waivePasswordPrompt = true
          this.waiveConfirmationModal = true
          this.waiveMessage = 'WAIVED PRICE ADJUSTMENT'
        }
      }

      if (this.$route.query.hasOwnProperty('waive') && this.$route.query.waive === '2') {
        if (!this.hasPermission('can_waive_health_card_fees')) {
          this.$router.go(-1)
        } else {
          this.canWaive = true
          this.waiveHealthCard = true
          this.waiveLevel = 2
          this.fields.waive_fee = true
          this.waiveHSAConfirmationModal = true
          this.waiveMessage = 'Insurance / HSA / FSA'
        }
      }

      if (this.$route.query.hasOwnProperty('waive') && this.$route.query.waive === '3') {
        if (!this.hasPermission('can_waive_debit_fees')) {
          this.$router.go(-1)
        } else {
          this.canWaive = true
          this.fields.waive_fee = true
          this.waiveLevel = 3
          this.waiveConfirmationModal = true
          this.waiveMessage = 'FOR DEBIT CARDS ONLY'
        }
      }

      // @TODO check on rerun to make sure it hasn't been voided already? Only if rerunning to recreate?

      if (this.$route.query.transaction || this.$route.query.rerun) {
        if (this.$route.query.transaction) {
          this.transactionId = this.$route.query.transaction
        } else {
          this.transactionId = this.$route.query.rerun
        }

        // @TODO should this be done on every reset?
        let data = await this.api({url: `/transactions/${this.transactionId}`})

        // @TODO check if the token has been disabled
        // if no token or !token_is_active, exit early?

        this.parentDetails = data.data.transaction
        if (!this.parentDetails.billing_name && this.parentDetails.card_name) {
          this.parentDetails.billing_name = this.parentDetails.card_name
        }
        this.parentCustomDetails = JSON.parse(data.data.transaction.custom)
        this.parentId = data.data.transaction.id

        this.fields.customer_po = this.parentDetails.customer_po
        this.fields.custom_field_1 = this.parentDetails.custom_field_1
        this.fields.custom_field_2 = this.parentDetails.custom_field_2
        this.fields.custom_field_3 = this.parentDetails.custom_field_3
        this.fields.notes = this.parentDetails.notes

        this.fields.billing_address = this.parentDetails.billing_address
        this.fields.billing_city = this.parentDetails.billing_city
        this.fields.billing_state = this.parentDetails.billing_state
        this.fields.billing_postalcode = this.parentDetails.billing_postal_code
        this.fields.billing_country = this.parentDetails.billing_country
        this.fields.billing_phone = this.parentDetails.billing_phone
        this.fields.billing_email = this.parentDetails.billing_email

        // PNX child runs don't persist full billing; backfill empty fields from the parent schedule.
        if (this.$route.query.schedule) {
          try {
            let scheduleData = await this.api({url: `/schedules/${this.$route.query.schedule}`})
            let schedule = scheduleData.data.schedule
            if (schedule) {
              if (!this.fields.billing_address) this.fields.billing_address = schedule.billing_address
              if (!this.fields.billing_city) this.fields.billing_city = schedule.billing_city
              if (!this.fields.billing_state) this.fields.billing_state = schedule.billing_state
              if (!this.fields.billing_postalcode) this.fields.billing_postalcode = schedule.billing_postal_code
              if (!this.fields.billing_country) this.fields.billing_country = schedule.billing_country
              if (!this.fields.billing_phone) this.fields.billing_phone = schedule.billing_phone
              if (!this.fields.billing_email) this.fields.billing_email = schedule.billing_email
            }
          } catch (e) {}
        }

        this.fields.ach_authorization = 'onfile'

        // Set terminal to parent if possible
        // @TODO account for disabled terminals, finding new ones if possible
        // if this terminal is set to can_charge = 0 and there is a similar terminal with can_charge = 1
        this.$refs.terminal.reset(this.parentDetails.terminal_id)

        if (this.$route.query.transaction) {
          if (typeof this.parentCustomDetails === 'object' && this.parentCustomDetails !== null && this.parentCustomDetails.hasOwnProperty('is_health_rewards_card') && this.parentCustomDetails.is_health_rewards_card) {
            this.canWaive = true
            this.waiveHealthCard = true
            this.waiveLevel = 2
            this.fields.waive_fee = true
            this.waiveHSAConfirmationModal = true
            this.waiveMessage = 'Insurance / HSA / FSA'
          }

          // user wants to adjust an installment, usually adding a payment
          if (this.$route.query.hasOwnProperty('i') && this.$route.query.i === 2) {
            // set the total amount to installment remaining balance less any price adjustment fees
            let totalRemaining = this.getInstallments(this.parentDetails)

            this.fields.recurring_total_amount = (((this.parentDetails.modify_total_amount ? this.parentDetails.modify_total_amount : this.parentDetails.total_amount) - (this.parentDetails.modify_fee_amount ? this.parentDetails.modify_fee_amount : this.parentDetails.fee_amount)) / 100 * totalRemaining.length).toFixed(2)

            this.fields.amount = 0.00

            this.fields.recurring = this.parentDetails.modify_recurring_type ? this.parentDetails.modify_recurring_type : this.parentDetails.recurring_type

            this.$refs.amountThreshold.$refs.textfield.focus()

            // display the previous schedule somewhere
          }
        } else if (this.$route.query.rerun) {
          this.fields.amount = ((this.parentDetails.total_amount - this.parentDetails.fee_amount - this.parentDetails.tax_amount) / 100).toFixed(2)

          this.fields.close_date = this.parentDetails.future_post ? Moment.utc(this.parentDetails.future_post).format('YYYY-MM-DD') : null

          this.fields.recurring = this.parentDetails.modify_recurring_type ? this.parentDetails.modify_recurring_type : this.parentDetails.recurring_type
          this.fields.recurring_start_date = this.parentDetails.modify_recurring_start ? Moment.utc(this.parentDetails.modify_recurring_start).format('YYYY-MM-DD') : Moment.utc(this.parentDetails.recurring_start).format('YYYY-MM-DD')

          if (this.parentDetails.modify_recurring_end) {
            this.fields.recurring_end_date = Moment.utc(this.parentDetails.modify_recurring_end).format('YYYY-MM-DD') === '2099-12-31' ? '' : Moment.utc(this.parentDetails.modify_recurring_end).format('YYYY-MM-DD')
            this.fields.recurring_ongoing = !!(Moment.utc(this.parentDetails.modify_recurring_end).format('YYYY-MM-DD') === '2099-12-31')
          } else if (this.parentDetails.is_recurring && !this.parentDetails.recurring_end) {
            this.fields.recurring_ongoing = true
          } else {
            this.fields.recurring_end_date = Moment.utc(this.parentDetails.recurring_end).format('YYYY-MM-DD') === '2099-12-31' ? '' : Moment.utc(this.parentDetails.recurring_end).format('YYYY-MM-DD')
            this.fields.recurring_ongoing = !!(Moment.utc(this.parentDetails.recurring_end).format('YYYY-MM-DD') === '2099-12-31')
          }
        }
      }
    },

    updateToken () {
      this.fields.card_name = this.parentDetails.billing_name
      const storedExp = this.parentDetails.card_expiration || this.parentDetails.card_expiration_yymm
      this.fields.card_expiration = storedExp && String(storedExp).length >= 4
        ? String(storedExp).slice(-2) + ' / ' + String(storedExp).slice(0, 2)
        : ''
      this.parentId = 0
      this.parentDetails = {}
      this.parentCustomDetails = {}
      this.updateCardInfo = !!this.updateCardInfo

      if (!this.updateCardInfo) {
        this.reset()
      }
    },

    clearToken () {
      this.fields.card_name = this.parentDetails.billing_name
      this.parentId = 0
      this.parentDetails = {}
      this.parentCustomDetails = {}
      this.newPaymentInfo = !!this.newPaymentInfo

      if (!this.newPaymentInfo) {
        this.reset()
      }
    },

    closeRerunModal () {
      this.rerunModal = false
    },

    openConfirmPaymentPlanModal () {
      if (!this.$refs.form1.validate()) {
        let errors = []
        this.$refs.form1.inputs.forEach(e => {
          if (e.hasError) {
            errors.push(e.errorBucket[0])
          }
        })

        let message = 'Validation Error'

        if (errors.length > 1) {
          message = 'Validation Errors'
        }

        Bus.$emit('openModal', {
          status: 'error',
          message: message,
          messageDetails: errors
        })

        return false
      }

      this.confirmPaymentPlanModal = true
    },

    closeConfirmPaymentPlanModal (confirm = false) {
      if (confirm) {
        this.recurringLocked = true
      } else {
        this.recurringLocked = false
      }
      this.confirmPaymentPlanModal = false
    },

    closeHSAConfirmationModal () {
      this.waiveHSAConfirmationModal = false
    },

    cancelWaiveModal () {
      this.$router.go(-1)
    },

    closeRecurringParentModal () {
      this.recurringParentModal = false
    },

    closeRecurringReceiptModal () {
      this.recurringReceiptModal = false
    },

    closeResult () {
      if (!this.isResponseError) {
        if (Object.keys(this.$route.query).length) {
          this.$router.replace('/credit')
        }

        this.reset()
      } else {
        this.resultModal = false

        this.isProcessing = false
        this.responseMessage = ''
        this.responseMessageDetails = ''
        this.isResponseError = false
      }
    },

    calculatePaymentCountWithRecurringAmount () {
      if (this.fields.recurring === 'none') {
        return 0
      }
      if (this.fields.recurring_start_date === '') {
        return 0
      }
      if (this.recurringRemainingBalance === '') {
        return 0
      }
      if (this.fields.recurring_amount === '' || this.fields.recurring_amount === 0 || this.fields.recurring_amount === 0.00) {
        return 0
      }

      let amount = this.recurringRemainingBalance / this.fields.recurring_amount
      return (amount === Infinity ? 0 : amount.toFixed(2))
    },

    setPaymentCount () {
      if (this.fields.recurring_ongoing) {
        // this.fields.recurring_end_date = ''
        this.$set(this.fields, 'recurring_end_date', '')
        // this.fields.recurring_payments = 0
        this.$set(this.fields, 'recurring_payments', 0)
      } else if (this.fields.recurring === 'none' || !this.fields.recurring_start_date || !this.fields.recurring_end_date) {
        this.$set(this.fields, 'recurring_payments', 0)
      } else {
        // this.fields.recurring_payments = Math.trunc(this.calculatePaymentCountWithEndDate())
        this.$set(this.fields, 'recurring_payments', Math.trunc(this.calculatePaymentCountWithEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_end_date)))
      }
    },

    setEndDate () {
      // this.fields.recurring_end_date = this.calculateEndDate()
      this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))
    },

    recurringAllowedEndDates (date) {
      if (!this.fields.recurring || this.fields.recurring === 'none') {
        return false
      }

      if (!this.fields.recurring_start_date) {
        return false
      }

      return this.allowedEndDates(this.fields.recurring, date, this.fields.recurring_start_date)
    },

    // @TODO centralize with ModifyInstallments
    handleChange (field) {
      if (field === 'balance' || field === 'frequency') {
        // keep balance / frequncy
        // calculate payments
        // keep amount
        // keep start_date
        // calculate end_date
        // calculate last_payment

        // calculate payments
        let paymentCount = this.calculatePaymentCountWithRecurringAmount()
        this.$set(this.fields, 'recurring_payments', Math.ceil(paymentCount))

        // calculate end_date
        this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))

        // add last payment if remainder
        if (Math.ceil(paymentCount) > paymentCount) {
          this.$set(this.fields, 'recurring_last_amount', Number(this.recurringRemainingBalance - (Math.floor(paymentCount) * this.fields.recurring_amount)).toFixed(2))
        } else {
          this.$set(this.fields, 'recurring_last_amount', null)
        }
      } else if (field === 'payments') {
        // keep balance / frequncy
        // keep payments
        // calculate amount
        // keep start_date
        // calculate end_date
        // calculate last_payment

        // calculate installment payment amount
        let subtotal = this.recurringRemainingBalance / this.fields.recurring_payments

        if (isNaN(subtotal) || subtotal === 0 || subtotal === Infinity) {
          return ''
        } else {
          this.$set(this.fields, 'recurring_amount', Number(subtotal).toFixed(2))
        }

        // calculate end date
        this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))

        // add last payment if remainder
        let potentialAmount = this.fields.recurring_payments * this.fields.recurring_amount
        if (potentialAmount > this.recurringRemainingBalance) {
          this.$set(this.fields, 'recurring_last_amount', Number(this.recurringRemainingBalance - ((this.fields.recurring_payments - 1) * this.fields.recurring_amount)).toFixed(2))
        } else {
          this.$set(this.fields, 'recurring_last_amount', null)
        }
      } else if (field === 'amount') {
        // keep balance / frequncy
        // keep payments
        // calculate amount
        // keep start_date
        // calculate end_date
        // calculate last_payment

        // calculate # of payments
        if (this.fields.recurring_ongoing) {
          this.fields.recurring_end_date = ''
          this.fields.recurring_payments = 0
        } else {
          let paymentCount = this.calculatePaymentCountWithRecurringAmount()
          // this.fields.payments = Math.ceil(paymentCount)
          this.$set(this.fields, 'recurring_payments', Math.ceil(paymentCount))

          // add last payment if remainder
          if (Math.ceil(paymentCount) > paymentCount) {
            // this.fields.last_amount = this.recurringRemainingBalance - (Math.floor(paymentCount) * this.fields.amount)
            this.$set(this.fields, 'recurring_last_amount', Number(this.recurringRemainingBalance - (Math.floor(paymentCount) * this.fields.recurring_amount)).toFixed(2))
          } else {
            this.$set(this.fields, 'recurring_last_amount', null)
          }
        }

        // calculate end date
        // this.fields.end_date = this.calculateEndDate()
        this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))
      } else if (field === 'start_date') {
        // calculate End Date
        // calculate # or payments
        // calculate installment payments
        // if remainder, last payment

        // calculate end date
        this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))
      } else if (field === 'end_date') {
        // calculate # of payments
        // calculate installment payments
        // no remainder, hide last payment
      }
    },

    printReceipt () {
      this.$refs.receipt.print()
    },

    printAuth () {
      this.$refs.achAuth.print()
    },

    printSchedule () {
      this.$refs.schedule.print()
    },

    async emailReceipt () {
      try {
        if (!this.transactionDetails || !this.transactionDetails.id) {
          throw new Error('Unable to identify transaction')
        }

        let result = await this.api({
          method: 'get',
          url: `/transactions/${this.transactionDetails.id}/send_receipt_email`
        })

        if (result.data && result.data.error === false) {
          Bus.$emit('openModal', {
            status: 'success',
            message: 'Email Sent'
          })
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Unexpected Error'
          })
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }
    },

    async emailSchedule () {
      try {
        if (!this.recurringTransactionDetails || !this.recurringTransactionDetails.id) {
          throw new Error('Unable to identify transaction')
        }

        let result = await this.api({
          method: 'get',
          url: `/plans/${this.recurringTransactionDetails.transaction_group_custom_tracking_id}/send_schedule_email`
        })

        if (result.data && result.data.error === false) {
          Bus.$emit('openModal', {
            status: 'success',
            message: 'Email Sent'
          })
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Unexpected Error'
          })
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }
    },

    // @TODO exports.validateManagerPassword = async (user_id, account_id, token_id, password) => {
    checkWaivePassword () {
      if (this.waiveLevel !== 3 && this.accountSelected.manager_password && !this.hasPermission('can_waive_without_password') && this.accountSelected.manager_password !== this.waivePassword) {
        this.waivePasswordError = ['Invalid Password']
      } else {
        this.waivePasswordError = []
        this.waiveConfirmationModal = false
      }
    },

    calculateCashDiscountFee (amount) {
      if (this.fields.waive_fee) {
        return 0
      }

      if (this.$route.name !== 'ChargeCard') {
        return 0
      }

      return this.calculateFee(this.selectedTerminal, amount)
    },

    getTotalWithCashDiscount (amount) {
      const subTotal = parseFloat(amount)
      const priceAdjustment = parseFloat(this.calculateCashDiscountFee(subTotal))
      const total = (subTotal + priceAdjustment)

      return {
        subTotal: Math.abs(subTotal).toFixed(2),
        priceAdjustment: Math.abs(priceAdjustment).toFixed(2),
        total: Math.abs(total).toFixed(2)
      }
    },

    showConfirmModal () {
      this.showConfirmationModal = true
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/terminal/Installment.vue